/**
 * FetchRoadmapItemLikedUsersUseCase
 *
 * Loads one page of users who liked (or disliked) a given roadmap item — the
 * owner-only RoadmapItemLikesBottomSheet. Thin delegate to
 * RoadmapItemLikesService.
 *
 * Flutter parity:
 *   - lib/presentation/bottom_sheet/roadmap_item_likes_bottom_sheet.dart:93-120
 *     (`_getData`) calls getRoadmapItemLikes(roadmapItemId, likedType,
 *     lastFetchedItem, AppConstants.roadmapItemLikesListPagination) and maps
 *     each row reading only `userId` + `userName`.
 *   - lib/data/repository/roadmap_repository.dart:102-109 forwards to the data
 *     source with no extra logic. Unlike the content liked-users sheet, Flutter
 *     applies NO `usersBlockedMe` filter here — the roadmap likes sheet has no
 *     blocked-user filtering — so this use case does NOT call
 *     shouldFilterOutBlockedUserUseCase. It stays a thin delegate.
 *   - Page size: AppConstants.roadmapItemLikesListPagination (= 9), byte-
 *     identical to SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT on web; we deliberately
 *     default to the shared web constant rather than inventing a per-screen one.
 *     See app_constants.dart:49.
 *
 * Errors from the service propagate — the hook layer logs via appLogger.error.
 */

import type { PageCursor } from '@/data/types/json';

import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import {
  roadmapItemLikesService,
  type RoadmapItemLikesService,
} from '@/domain/services/roadmap/roadmapItemLikesService';

export interface FetchRoadmapItemLikedUsersParams {
  roadmapItemId: string;
  liked: boolean;
  pageSize?: number;
  cursor?: PageCursor;
}

export interface FetchRoadmapItemLikedUsersResult {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class FetchRoadmapItemLikedUsersUseCase {
  private readonly likesService: RoadmapItemLikesService;

  constructor(likesService?: RoadmapItemLikesService) {
    this.likesService = likesService ?? roadmapItemLikesService;
  }

  async execute(
    params: FetchRoadmapItemLikedUsersParams,
  ): Promise<FetchRoadmapItemLikedUsersResult> {
    const { roadmapItemId, liked, pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT, cursor } =
      params;

    const page = await this.likesService.fetchRoadmapItemLikedUsers(
      roadmapItemId,
      liked,
      pageSize,
      cursor,
    );

    return {
      likedUsers: page.likedUsers,
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }
}

export const fetchRoadmapItemLikedUsersUseCase = new FetchRoadmapItemLikedUsersUseCase();
